import { useEffect, useCallback, useContext, useState, useRef } from 'react';

import { expVal, expValEquals } from '@confluence/feature-experiments';
import { useIsAIEnabledForContent } from '@confluence/ai-common/entry-points/useIsAIEnabledForContent';
import { AI_TITLE_GENERATION } from '@confluence/experience-tracker/entry-points/ExperienceName';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component';

interface UseEditorPasteDetectionProps {
	contentId: string;
	cloudId: string;
	title: string | null;
	isEditorEmpty: boolean;
	onTitleChange?: (title: string) => void;
	userOffline?: boolean;
}

interface UseEditorPasteDetectionReturn {
	shouldGenerateTitle: boolean;
	generateTitleFromPaste: (proseMirrorElement: HTMLElement | null) => void;
	isTitleGenerating: boolean;
	shouldShowStreaming: boolean;
	documentContext: string;
	onStreamingComplete: () => void;
	onStreamingError: () => void;
}

export const useEditorPasteDetection = ({
	contentId,
	title,
	isEditorEmpty,
	userOffline,
}: UseEditorPasteDetectionProps): UseEditorPasteDetectionReturn => {
	const { isAIEnabledForContent } = useIsAIEnabledForContent({ contentId });
	const experienceTracker = useContext(ExperienceTrackerContext);
	const [isTitleGenerating, setIsTitleGenerating] = useState(false);
	const [shouldShowStreaming, setShouldShowStreaming] = useState(false);
	const timeoutRef = useRef<NodeJS.Timeout | null>(null);
	const proseMirrorElementRef = useRef<HTMLElement | null>(null);
	const titleRef = useRef(title);
	const wasEditorEmptyRef = useRef<boolean>(true);

	useEffect(() => {
		wasEditorEmptyRef.current = isEditorEmpty;
	}, [isEditorEmpty]);

	useEffect(() => {
		titleRef.current = title;
	}, [title]);

	const isFeatureEnabled =
		isAIEnabledForContent &&
		!userOffline &&
		expValEquals('confluence_ai_title_generation_on_paste_standard', 'isEnabled', true);

	const [documentContext, setDocumentContext] = useState<string>('');

	const findProseMirrorElement = useCallback((): HTMLElement | null => {
		const filteredProseMirrors = Array.from(document.querySelectorAll('.ProseMirror')).filter(
			(el) => !el.closest('[data-testid="chromeless-editor"]'),
		);
		return filteredProseMirrors[0] as HTMLElement | null;
	}, []);

	const generateTitleFromPaste = useCallback(
		(proseMirrorElement: HTMLElement | null) => {
			const currentTitle = titleRef.current;

			if (!isFeatureEnabled || (currentTitle && currentTitle.trim() !== '')) {
				return;
			}

			if (!proseMirrorElement) {
				return;
			}

			const extractedContext = proseMirrorElement.textContent || '';

			setIsTitleGenerating(true);
			experienceTracker?.start({
				name: AI_TITLE_GENERATION,
			});

			setDocumentContext(extractedContext);
			setShouldShowStreaming(true);
		},
		[isFeatureEnabled, experienceTracker],
	);

	useEffect(() => {
		// Only check if feature is enabled, not if title is empty
		// This keeps the listener active even after a title is generated
		if (!isFeatureEnabled) {
			return;
		}

		const proseMirrorElement = findProseMirrorElement();

		if (!proseMirrorElement) {
			return;
		}

		const handlePaste = (event: ClipboardEvent) => {
			const pastedText = event.clipboardData?.getData('text/plain') || '';
			const wasEditorEmpty = wasEditorEmptyRef.current;

			if (
				wasEditorEmpty &&
				pastedText.length >
					expVal('confluence_ai_title_generation_on_paste_standard', 'charLength', 50)
			) {
				if (timeoutRef.current) {
					clearTimeout(timeoutRef.current);
				}

				timeoutRef.current = setTimeout(() => {
					generateTitleFromPaste(proseMirrorElement);
					timeoutRef.current = null;
				}, 100);
			}
		};

		proseMirrorElementRef.current = proseMirrorElement;

		proseMirrorElement.addEventListener('paste', handlePaste);

		return () => {
			proseMirrorElement.removeEventListener('paste', handlePaste);

			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
				timeoutRef.current = null;
			}
		};
	}, [findProseMirrorElement, isFeatureEnabled, generateTitleFromPaste]);

	const onStreamingComplete = useCallback(() => {
		setIsTitleGenerating(false);
		setShouldShowStreaming(false);
	}, []);

	const onStreamingError = useCallback(() => {
		setIsTitleGenerating(false);
		setShouldShowStreaming(false);
	}, []);

	return {
		shouldGenerateTitle: isFeatureEnabled && (!title || title.trim() === '') && !isEditorEmpty,
		generateTitleFromPaste,
		isTitleGenerating,
		shouldShowStreaming,
		documentContext,
		onStreamingComplete,
		onStreamingError,
	};
};
